import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drama, Search, UserCog, GraduationCap, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ALL_CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const ALL_SECTIONS = ["A", "B", "C", "D", "E", "F"];
const classLabel = (c) => (["Play Group", "LKG", "UKG"].includes(c) ? c : `Class ${c}`);

/** Floating "Simulate role" trigger — only rendered for the DevAdmin account. */
export function SimulateButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user?.is_dev_admin) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white px-4 py-2.5 shadow-lg shadow-fuchsia-600/30 text-sm font-medium"
        data-testid="devadmin-simulate-btn"
      >
        <Drama className="h-4 w-4" /> Simulate role
      </button>
      <SimulateDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

function SimulateDialog({ open, onOpenChange }) {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState("staff"); // "staff" | "student"
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = { type, q };
        if (type === "student") {
          if (classFilter) params.class_name = classFilter;
          if (sectionFilter) params.section = sectionFilter;
        }
        const { data } = await api.get("/devadmin/targets", { params });
        setRows(data.targets || []);
      } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Search failed"); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [open, type, q, classFilter, sectionFilter]);

  const pick = async (target) => {
    setBusyId(target.id);
    const res = await impersonate(type, target.id);
    setBusyId(null);
    if (!res.ok) { toast.error(res.error || "Could not simulate"); return; }
    onOpenChange(false);
    toast.success(`Now simulating ${target.name}`);
    navigate("/");
  };

  const TypeTab = ({ val, icon: Icon, label }) => (
    <button
      onClick={() => { setType(val); setRows([]); setQ(""); setClassFilter(""); setSectionFilter(""); }}
      className={`flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-colors ${type === val ? "bg-fuchsia-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}
      data-testid={`sim-tab-${val}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="simulate-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}><Drama className="inline h-5 w-5 mr-2 -mt-1 text-fuchsia-600" /> Simulate a role</DialogTitle>
          <DialogDescription>Log in as any teacher, admin or student to see exactly what they see. You can exit anytime.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <TypeTab val="staff" icon={UserCog} label="Teacher / Admin" />
          <TypeTab val="student" icon={GraduationCap} label="Student" />
        </div>

        {type === "student" && (
          <div className="grid grid-cols-2 gap-2" data-testid="sim-class-section">
            <Select value={classFilter} onValueChange={(v) => setClassFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="sim-class"><SelectValue placeholder="All classes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {ALL_CLASSES.map((c) => <SelectItem key={c} value={c}>{classLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sectionFilter} onValueChange={(v) => setSectionFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 rounded-xl" data-testid="sim-section"><SelectValue placeholder="All sections" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {ALL_SECTIONS.map((s) => <SelectItem key={s} value={s}>Section {s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={type === "staff" ? "Search by name or phone…" : "Search by name or roll…"} className="h-11 rounded-xl pl-9" data-testid="sim-search" />
        </div>

        <div className="max-h-72 overflow-y-auto space-y-1.5" data-testid="sim-results">
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Searching…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">No matches.</div>
          ) : rows.map((r) => (
            <button
              key={r.id}
              onClick={() => pick(r)}
              disabled={busyId === r.id}
              className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-fuchsia-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/20 flex items-center justify-between gap-2 transition-colors"
              data-testid={`sim-target-${r.id}`}
            >
              <div>
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{r.name}</div>
                <div className="text-xs text-slate-500">
                  {type === "staff"
                    ? `${(r.role || "teacher").replace("_", " ")}${r.phone ? " · " + r.phone : ""}`
                    : `${r.class_name || ""} ${r.section || ""} · Roll ${r.roll_number || "—"}`}
                </div>
              </div>
              {busyId === r.id && <Loader2 className="h-4 w-4 animate-spin text-fuchsia-600" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Persistent top banner shown while a simulation is active. */
export function SimulationBanner() {
  const { user, student, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const active = user?.impersonated || student?.impersonated;
  if (!active) return null;
  const name = user?.name || student?.name || "user";

  const exit = async () => {
    setBusy(true);
    const res = await stopImpersonating();
    setBusy(false);
    if (!res.ok) { toast.error(res.error || "Could not exit"); return; }
    toast.success("Exited simulation");
    navigate("/");
  };

  return (
    <div className="sticky top-0 z-50 bg-fuchsia-600 text-white text-sm" data-testid="simulation-banner">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 truncate">
          <Drama className="h-4 w-4 shrink-0" /> Simulating as <span className="font-semibold">{name}</span>
        </span>
        <button onClick={exit} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-3 py-1 font-medium shrink-0" data-testid="simulation-exit-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Exit simulation
        </button>
      </div>
    </div>
  );
}
